// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Chip, FileButton, Group, Input, Select, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { POLICYHOLDER_LABELS } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useId, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { searchInsurancePayers } from '../../lib/insurance-payers';
import type { CoverageAnswers, Policyholder } from './answers';
import { useIntakeV3Answers } from './answers';
import type { CardPhoto } from './card-photo';
import { prepareCardPhoto } from './card-photo';
import { parseDateOfBirth } from './eligibility';
import { controlClasses, FieldError, Note, optionalLabel } from './IntakeV3Controls';
import { IntakeV3Frame } from './IntakeV3Frame';
import { AlertIcon, ArrowIcon, CameraIcon, CheckIcon, CloseIcon, ShieldIcon } from './icons';
import type { PlanChoice, PlanPayer } from './plans';
import { getPlanOption, getPlanPayer, OTHER_PLAN, PLAN_OPTIONS, selectPlanPayer } from './plans';
import { Question } from './Question';
import type { IntakeV3StepProps } from './steps';

const POLICYHOLDER_OPTIONS = Object.entries(POLICYHOLDER_LABELS).map(([value, label]) => ({
  value: value as Policyholder,
  label,
}));

const COVERED_PLANS_SENTENCE = PLAN_OPTIONS.filter((option) => option.value !== OTHER_PLAN)
  .map((option) => option.label)
  .join(', ')
  .replace(/, ([^,]+)$/, ', and $1');

function CardPhotoInput({
  label,
  title,
  hint,
  photo,
  onChange,
}: {
  /** Names the controls and the photo, e.g. "Front of card". */
  label: string;
  /** Shown on the drop zone, e.g. "Insurance card — front". */
  title: string;
  hint: string;
  photo: CardPhoto | undefined;
  onChange: (photo: CardPhoto | undefined) => void;
}): JSX.Element {
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | null): Promise<void> => {
    if (!file) {
      return;
    }
    setReading(true);
    setError(null);
    try {
      onChange(await prepareCardPhoto(file));
    } catch (err) {
      Sentry.captureException(err);
      setError("We couldn't read that photo. Try another one.");
    } finally {
      setReading(false);
    }
  };

  return (
    <Stack gap={0} style={{ flex: '1 1 0', minWidth: 0 }}>
      {photo ? (
        <div className={controlClasses.upDone}>
          <img src={photo.dataUrl} alt={label} className={controlClasses.upImg} />
          <span className={controlClasses.upOk}>
            <CheckIcon />
            {title}
          </span>
          <button
            type="button"
            className={controlClasses.upRemove}
            aria-label={`Remove ${label.toLowerCase()}`}
            onClick={() => onChange(undefined)}
          >
            <CloseIcon />
          </button>
        </div>
      ) : (
        <FileButton onChange={handleFile} accept="image/*" capture="environment">
          {(props) => (
            <button
              {...props}
              type="button"
              className={controlClasses.up}
              aria-label={`Add ${label.toLowerCase()}`}
              aria-busy={reading || undefined}
              disabled={reading}
            >
              <CameraIcon />
              <span>{title}</span>
              <small>{hint}</small>
              <small className={controlClasses.upCta}>
                {reading ? 'Reading the photo…' : 'Take a photo or choose a file'}
              </small>
            </button>
          )}
        </FileButton>
      )}
      {error && (
        <div role="alert">
          <FieldError>{error}</FieldError>
        </div>
      )}
    </Stack>
  );
}

/**
 * Primary plan for an insured patient: the plan, card photos, member and group numbers, and
 * who holds the policy. The plan resolves to its exact payer Organization before moving on,
 * so the Coverage created at submit never points at a near match.
 */
export function CoverageStep({ step, onBack, onContinue }: IntakeV3StepProps): JSX.Element {
  const { answers, updateAnswers } = useIntakeV3Answers();
  const coverage = answers.coverage;
  const [attempted, setAttempted] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(false);
  const policyholderDateOfBirthId = useId();

  const plan = getPlanOption(coverage.plan);
  const needsPlanPayer = (plan?.payers.length ?? 0) > 1;
  const planPayer: PlanPayer | undefined = getPlanPayer(coverage.plan, coverage.planPayer);
  const isOtherPlan = coverage.plan === OTHER_PLAN;
  const showDetails = !!plan && !isOtherPlan;
  // Parents and guardians booking for a minor usually hold the policy themselves.
  const policyholder = coverage.policyholder ?? (answers.eligibility.minor ? 'parent' : undefined);
  const notSelf = !!policyholder && policyholder !== 'self';

  const errors = {
    plan: !plan ? 'Choose your plan' : null,
    planPayer: needsPlanPayer && !planPayer ? 'Choose your Blue Cross Blue Shield plan' : null,
    memberId: !coverage.memberId?.trim() ? 'Enter your member ID' : null,
    policyholder: !policyholder ? 'Choose who holds the policy' : null,
    policyholderName: notSelf && !coverage.policyholderName?.trim() ? 'Enter the name on the policy' : null,
    policyholderDateOfBirth:
      notSelf && !parseDateOfBirth(coverage.policyholderDateOfBirth) ? 'Enter a valid date of birth, MM/DD/YYYY' : null,
  };
  const shown = (key: keyof typeof errors): string | undefined => (attempted ? (errors[key] ?? undefined) : undefined);

  const update = (patch: Partial<CoverageAnswers>): void => updateAnswers('coverage', patch);

  const resolvePayer = async (payer: PlanPayer): Promise<NonNullable<CoverageAnswers['payer']>> => {
    if (coverage.payer?.key === payer.key) {
      return coverage.payer;
    }
    const match = selectPlanPayer(await searchInsurancePayers(payer.name), payer);
    return { key: payer.key, id: match.id, name: match.display };
  };

  const handleContinue = async (): Promise<void> => {
    setAttempted(true);
    setResolveError(false);
    if (isOtherPlan || !planPayer || Object.values(errors).some(Boolean)) {
      return;
    }
    setResolving(true);
    try {
      const payer = await Sentry.startSpan({ op: 'http.client', name: 'Resolve intake plan payer' }, () =>
        resolvePayer(planPayer)
      );
      update({ payer, policyholder });
      onContinue();
    } catch (err) {
      Sentry.captureException(err);
      setResolveError(true);
    } finally {
      setResolving(false);
    }
  };

  return (
    <IntakeV3Frame
      currentSection={step.section}
      title={showDetails ? `Your ${plan.label} details` : 'Your insurance details'}
      description="Our team verifies your coverage before your visit and emails you an estimate. Nothing is charged today."
      onBack={onBack}
      actions={
        <Button rightSection={<ArrowIcon />} loading={resolving} onClick={handleContinue}>
          Continue
        </Button>
      }
    >
      <Stack gap={16}>
        <Stack gap={12}>
          <Select
            label="Insurance plan"
            placeholder="Search or choose your plan"
            data={PLAN_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
            searchable
            value={coverage.plan ?? null}
            onChange={(value: string | null) =>
              update({ plan: (value ?? undefined) as PlanChoice | undefined, planPayer: undefined, payer: undefined })
            }
            error={shown('plan')}
          />
          {needsPlanPayer && plan && (
            <Select
              label={`Which ${plan.label} plan?`}
              placeholder="Choose the name on your card"
              data={plan.payers.map((payer) => ({ value: payer.key, label: payer.label }))}
              value={coverage.planPayer ?? null}
              onChange={(value: string | null) => update({ planPayer: value ?? undefined, payer: undefined })}
              error={shown('planPayer')}
            />
          )}
          {showDetails && (
            <Note icon={<ShieldIcon />}>
              Polaris works with {plan.label}. We'll confirm your coverage before your visit.
            </Note>
          )}
          {isOtherPlan && (
            <Note tone="orange" icon={<AlertIcon />}>
              Polaris works with {COVERED_PLANS_SENTENCE}. We don't take other plans yet. Go back and choose "I'll pay
              myself" to book as self-pay.
            </Note>
          )}
        </Stack>

        {showDetails && (
          <>
            <div className={controlClasses.upload}>
              <CardPhotoInput
                label="Front of card"
                title="Insurance card — front"
                hint="Member ID and group number are usually here"
                photo={coverage.cardFront}
                onChange={(cardFront) => update({ cardFront })}
              />
              <CardPhotoInput
                label="Back of card"
                title="Insurance card — back"
                hint="Payer ID and claims address"
                photo={coverage.cardBack}
                onChange={(cardBack) => update({ cardBack })}
              />
            </div>

            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing={12} verticalSpacing={16}>
              <TextInput
                label="Member ID"
                value={coverage.memberId ?? ''}
                onChange={(event) => update({ memberId: event.currentTarget.value })}
                error={shown('memberId')}
                autoComplete="off"
              />
              <TextInput
                label={optionalLabel('Group number')}
                value={coverage.groupNumber ?? ''}
                onChange={(event) => update({ groupNumber: event.currentTarget.value })}
                autoComplete="off"
              />
            </SimpleGrid>

            <Question label="Who is the policyholder?" error={shown('policyholder')}>
              <Chip.Group
                multiple={false}
                value={policyholder ?? null}
                onChange={(value: string) => update({ policyholder: value as Policyholder })}
              >
                <Group gap={8}>
                  {POLICYHOLDER_OPTIONS.map((option) => (
                    <Chip key={option.value} value={option.value} variant="outline">
                      {option.label}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </Question>

            {notSelf && (
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing={12} verticalSpacing={16}>
                <TextInput
                  label="Name on the policy"
                  value={coverage.policyholderName ?? ''}
                  onChange={(event) => update({ policyholderName: event.currentTarget.value })}
                  error={shown('policyholderName')}
                  autoComplete="off"
                />
                <Input.Wrapper
                  id={policyholderDateOfBirthId}
                  label="Policyholder date of birth"
                  description={
                    shown('policyholderDateOfBirth') ? undefined : 'Coverage is checked under the policyholder.'
                  }
                  error={shown('policyholderDateOfBirth')}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                >
                  <Input
                    component={IMaskInput}
                    mask="00/00/0000"
                    placeholder="MM/DD/YYYY"
                    inputMode="numeric"
                    autoComplete="off"
                    id={policyholderDateOfBirthId}
                    value={coverage.policyholderDateOfBirth ?? ''}
                    onAccept={(value: string) => update({ policyholderDateOfBirth: value })}
                    error={!!shown('policyholderDateOfBirth')}
                  />
                </Input.Wrapper>
              </SimpleGrid>
            )}
          </>
        )}

        {resolveError && (
          <Note tone="bad" icon={<AlertIcon />}>
            We couldn't confirm your plan right now. Try again in a moment.
          </Note>
        )}
      </Stack>
    </IntakeV3Frame>
  );
}
